/**
 * Master Data Repository
 *
 * home_karte_api のマスタAPIとの通信を担当。
 * billing_types, account_types 等のマスタデータを取得する。
 */

import { ENV } from '../config/env';
import { createApiClient } from './api';
import type { ApiResult } from '../types/api';

const API_BASE_URL = ENV.API_BASE_URL;
const USE_MOCK = !API_BASE_URL;

/** マスタアイテム */
export interface MasterItem {
  itemId: string;
  itemName: string;
  itemCode?: string;
  itemRate?: number;
  itemStartAt?: string;
}

/** マスタカテゴリ */
export interface MasterCategory {
  id: string;
  name: string;
  item: MasterItem[];
}

/** マスタAPIレスポンス */
interface MasterApiResponse {
  items: MasterCategory[];
}

/** マスタキー定数 */
export const MASTER_KEY = {
  ACCOUNT_TYPES: 'account_types',
  BILLING_TYPES: 'billing_types',
  PREFECTURES: 'prefectures',
} as const;

export const masterRepository = {
  /**
   * マスタデータ全取得
   */
  getAll: async (token?: string): Promise<ApiResult<MasterCategory[]>> => {
    if (USE_MOCK) {
      await new Promise(resolve => setTimeout(resolve, 100));
      return {
        success: true,
        data: [
          {
            id: MASTER_KEY.ACCOUNT_TYPES,
            name: '顧客種別',
            item: [
              { itemId: 'mock-at-1', itemName: '個人', itemCode: 'individual' },
              { itemId: 'mock-at-2', itemName: '法人', itemCode: 'corporation' },
            ],
          },
          {
            id: MASTER_KEY.BILLING_TYPES,
            name: '請求先区分',
            item: [
              { itemId: 'mock-bt-1', itemName: '本社' },
              { itemId: 'mock-bt-2', itemName: '支店' },
              { itemId: 'mock-bt-3', itemName: '個人' },
            ],
          },
          {
            id: MASTER_KEY.PREFECTURES,
            name: '都道府県',
            item: [
              { itemId: 'mock-pref-13', itemName: '東京都' },
              { itemId: 'mock-pref-14', itemName: '神奈川県' },
              { itemId: 'mock-pref-27', itemName: '大阪府' },
            ],
          },
        ],
      };
    }

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const client = createApiClient({ baseUrl: API_BASE_URL, headers });
    const result = await client.get<MasterApiResponse>('/api/v1/master');

    if (!result.success) {
      return result;
    }

    return { success: true, data: result.data.items };
  },
};
